import type { ContextType } from "react";
import { Component } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { Dropdown, DropdownButton } from "@similarweb/ui-components";

import { ECategoryType } from "app/common/services/categoryService.types";

import { TrackContext } from "../../WithTrack/src/context";
import { useTranslation } from "../../WithTranslation/src/I18n";
import type { ITranslateProp } from "../../WithTranslation/src/TranslationProvider";

import { WebsiteListTypeSelectorItem } from "./WebsiteListTypeSelectorItem";

const ListTypeIconWrapper = styled.div`
    margin: -14px 8px 0 0;
`;

const WEBSITE_LIST_TYPES = {
    General: {
        id: ECategoryType.GENERAL_LIST,
        title: "websites.lists.types.general",
        description: "websites.lists.types.general.description",
    },
    PartnersList: {
        id: ECategoryType.PARTNERS_LIST,
        title: "websites.lists.types.partners",
        description: "websites.lists.types.partners.description",
    },
};

export interface IWebsiteListTypeSelectorProps {
    onListTypeSelect: (typeId) => void;
    buttonWidth?: string;
    containerClass?: string;
    dropdownPopupPlacement?: string;
    disabled?: boolean;
    appendTo?: string;
    selectedListType?: string;
}

interface IWebsiteListTypeSelectorState {
    selectedListType: string;
}

type WebsiteListTypeSelectorBaseProps = IWebsiteListTypeSelectorProps & ITranslateProp;

class WebsiteListTypeSelectorBase extends Component<WebsiteListTypeSelectorBaseProps, IWebsiteListTypeSelectorState> {
    static contextType = TrackContext;

    context: ContextType<typeof TrackContext>;

    private static defaultProps = {
        dropdownPopupPlacement: "bottom-left",
    };

    private getListTypeTextById = (listId) => {
        switch (listId) {
            case ECategoryType.GENERAL_LIST:
                return this.props.translate(WEBSITE_LIST_TYPES.General.title);
            case ECategoryType.PARTNERS_LIST:
                return this.props.translate(WEBSITE_LIST_TYPES.PartnersList.title);
            default:
                return this.props.translate("websites.lists.types.undefined");
        }
    };

    private onListTypeSelectProxy = (selectedTypeId) => {
        return () => {
            this.context.track("Drop Down", "click", `Website List Type/${selectedTypeId}`);
            this.setState({ selectedListType: selectedTypeId });
            this.props.onListTypeSelect(selectedTypeId);
        };
    };

    constructor(props) {
        super(props);

        this.state = {
            selectedListType: props.selectedListType,
        };
    }

    public render() {
        const { buttonWidth, containerClass, dropdownPopupPlacement, disabled, translate } = this.props;
        const cssClassContainer = `DropdownContent-container Popup-content--pro-dropdown ${containerClass}`;
        let selectedTypeIcon = null;
        switch (this.state.selectedListType) {
            case ECategoryType.GENERAL_LIST:
                selectedTypeIcon = "custom-category";
                break;
            case ECategoryType.PARTNERS_LIST:
                selectedTypeIcon = "partners-list";
                break;
        }

        const renderList = () => {
            return (
                <div>
                    {GenericType(this.onListTypeSelectProxy(ECategoryType.GENERAL_LIST), translate)}
                    {PartnerType(this.onListTypeSelectProxy(ECategoryType.PARTNERS_LIST), translate)}
                </div>
            );
        };

        return (
            <Dropdown
                appendTo={this.props.appendTo}
                cssClassContainer={cssClassContainer}
                dropdownPopupPlacement={dropdownPopupPlacement}
                buttonWidth={buttonWidth}
            >
                <DropdownButton
                    cssClass="DropdownButton--filtersBarDropdownButton"
                    height="initial"
                    disabled={disabled}
                    key="websiteListSelectorButton"
                >
                    {selectedTypeIcon ? (
                        <ListTypeIconWrapper>
                            <SWReactIcons
                                size="xs"
                                iconName={selectedTypeIcon}
                            />
                        </ListTypeIconWrapper>
                    ) : null}
                    <div>{this.getListTypeTextById(this.state.selectedListType)}</div>
                </DropdownButton>
                {renderList()}
            </Dropdown>
        );
    }
}

export const WebsiteListTypeSelector = (props: IWebsiteListTypeSelectorProps) => {
    const translate = useTranslation();

    return (
        <WebsiteListTypeSelectorBase
            {...props}
            translate={translate}
        />
    );
};

const GenericType = (onClick, translate) => {
    return (
        <WebsiteListTypeSelectorItem
            closePopupOnClick={true}
            preventDefault={true}
            onClick={onClick}
            iconName="custom-category"
            description={translate(WEBSITE_LIST_TYPES.General.description)}
            title={translate(WEBSITE_LIST_TYPES.General.title)}
        />
    );
};

const PartnerType = (onClick, translate) => {
    return (
        <WebsiteListTypeSelectorItem
            closePopupOnClick={true}
            preventDefault={true}
            onClick={onClick}
            iconName="partners-list"
            description={translate(WEBSITE_LIST_TYPES.PartnersList.description)}
            title={translate(WEBSITE_LIST_TYPES.PartnersList.title)}
        />
    );
};
